import React, { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

// MUI
import {
  ThemeProvider as MuiThemeProvider,
  useTheme,
} from "@mui/material/styles";
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import Typography from "@mui/material/Typography";
import {
  Box,
  IconButton,
  SwipeableDrawer,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";

// MUI Icons
import MenuIcon from "@mui/icons-material/Menu";
import Close from "@mui/icons-material/Close";

// Custom Components
import { appHeaderTheme } from "@/components/ThemeContext/ThemeProvider";
import LanguageSelector from "@/ui/Header/LanguageSelector";
import AccessibilityMenu from "@/ui/Header/AccessibilityMenu";
import LogoIcon from "@/icons/LogoIcon";
import UserContext from "@/components/UserContext/UserContext";
import ProfileMenu from "@/ui/Header/ProfileMenu"

const GradientDivider = (props: { type: string }) => {
  if (props.type === "vertical") {
    return (
      <Box
        sx={{
          height: "35px",
          width: "1px",
          margin: "5px 17px",
          background:
            "linear-gradient(to bottom, rgba(255,255,255,1.0), rgba(255,255,255,1.0))",
        }}
      />
    );
  } else if (props.type === "horizontal") {
    return (
      <Box
        sx={{
          height: "1px",
          width: "100%",
          background:
            "linear-gradient(to bottom, rgba(255,255,255,1.0), rgba(255,255,255,1.0))",
          margin: 2,
          justifySelf: "center",
          alignSelf: "center",
        }}
      />
    );
  } else return <></>;
};

const pages = [
  {
    title: "public.map",
    route: "/",
  },
  {
    title: "public.izleti",
    route: "/sigurni-izleti",
  },
  {
    title: "public.headings.safety_advice",
    route: "/sigurnosni-savjeti",
  },
  {
    title: "public.headings.mobile_app",
    route: "/mobilna-aplikacija",
  },
  {
    title: "public.data_sources_title",
    route: "/izvori-podataka",
  },
  {
    title: "public.project_info",
    route: "/info",
  },
  // {
  //   title: "public.headings.mobile_app",
  //   route: "/mobilna-aplikacija",
  // },
];

const PublicHeader = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const userContext = React.useContext(UserContext);
  const isLoggedIn = !!(userContext && userContext.id);
  const theme = useTheme();

  const lgUp = useMediaQuery(theme.breakpoints.up("lg"));

  const toolbarStyle = {
    "& > button": {
      mx: 1,
    },
    borderTop: 0,
    borderLeft: 0,
    borderRight: 0,
    // borderBottom: "2px",
    borderStyle: "solid",
    borderColor: "primary.main",
    display: "flex",
    justifyContent: "space-between",
    flexDirection: "row",
    padding: 1,
  };

  const [showMobileSideBar, setShowMobileSideBar] = useState<boolean>(false);

  const toggleDrawer = (state: boolean) => () => {
    setShowMobileSideBar(state);
  };

  return (
    <header id="header" style={{ flexGrow: 1 }}>
      <MuiThemeProvider
        theme={(outerTheme) => ({
          ...outerTheme,
          palette: {
            ...outerTheme.palette,
            secondary: appHeaderTheme.palette.secondary,
            // 'common.white' is used by shared header buttons (language, accessibility, profile);
            // override it here so they pick up the public header's accent color
            common: {
              ...outerTheme.palette.common,
              white: outerTheme.palette.mint.main,
            },
          },
        })}
      >
        <AppBar
          position="fixed"
          color="secondary"
          sx={{ backgroundColor: "primary.main" }}
        >
          <Toolbar variant="dense" sx={toolbarStyle}>
            <Box
              alignContent={""}
              sx={{
                display: "flex",
                flexDirection: "row",
                width: { xs: "100%", lg: "auto" },
              }}
            >
              <LogoIcon
                fontSize="large"
                sx={{
                  mr: { xs: 0, lg: 3 },
                  transform: "scale(1.14)",
                  ml: { xs: 1, lg: 0 },
                  height: "auto",
                  color: "mint.main",
                }}
                onClick={() => {
                  navigate("/public");
                }}
              />
              <Typography
                textAlign={"center"}
                variant="h6"
                component="h2"
                sx={{ color: "mint.main", fontSize: "1.75rem", flexGrow: 1 }}
              >
                <NavLink
                  to="/public"
                  style={{
                    color: theme.palette.mint.main,
                    textDecoration: "none",
                  }}
                >
                  {t("public_app_title")}
                </NavLink>
              </Typography>
            </Box>

            {/* DESKTOP BUTTONS*/}
            <Box
              sx={{
                display: { xs: "none", lg: "flex" },
                alignItems: "center",
              }}
            >
              <Box display={"flex"} gap={"10px"}>
                {pages.map((page) => {
                  return (
                    <NavLink
                      key={page.title+"-nav-link"}
                      id={"public_desktop_navlink"}
                      to={page.route}
                      title={t(page.title)}
                      style={({ isActive }) => {
                        return {
                          color: theme.palette.mint.main,
                          textDecoration: "none",
                          padding: "5px",
                          fontWeight: isActive ? 600 : 400,
                        };
                      }}
                    >
                      <span>{t(page.title)}</span>
                    </NavLink>
                  );
                })}
              </Box>

              <GradientDivider type="vertical" />
              <Box display={"flex"} alignItems={"center"} gap={0.5}>
                <LanguageSelector />
                {/* <HeaderIconButton onClick={() => { navigate('/login') }} desc="login">
                  <LoginIcon />
                </HeaderIconButton> */}
                <AccessibilityMenu />
                {isLoggedIn ? <ProfileMenu/> : null}
              </Box>
            </Box>

            <Box sx={{ display: { xs: "flex", lg: "none" }}}>
              <IconButton
                size="small"
                sx={{ color: "mint.main" }}
                onClick={toggleDrawer(true)}
              >
                <MenuIcon fontSize="large"></MenuIcon>
              </IconButton>
            </Box>
          </Toolbar>
          <SwipeableDrawer
            disableSwipeToOpen
            sx={{ zIndex: 1299 }}
            PaperProps={{
              sx: { backgroundColor: theme.palette.primary.main, width: "75%" },
            }}
            anchor="right"
            open={!lgUp && showMobileSideBar}
            onClose={toggleDrawer(false)}
            onOpen={toggleDrawer(true)}
          >
            <Box
              bgcolor={"secondary"}
              sx={{
                position: "relative",
                display: "flex",
                width: "100%",
                flexDirection: "column",
                justifyContent: "center",
                flex: 1,
              }}
            >
              <IconButton
                size="small"
                onClick={toggleDrawer(false)}
                sx={{ position: "absolute", top: 0, right: 0, color: "mint.main" }}
              >
                <Close fontSize="large"></Close>
              </IconButton>

              {/* MOBILE BUTTONS */}
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                  padding: 3,
                  fontSize: "1.2rem",
                }}
              >
                {pages.map((page) => (
                  <NavLink
                    key={"desktop_nav_" + page.title}
                    id={"public_desktop_navlink"}
                    to={page.route}
                    style={({ isActive }) => {
                      return {
                        display: "flex",
                        alignItems: "center",
                        textDecoration: "none",
                        color: theme.palette.mint.main,
                        marginTop: "3px",
                        fontWeight: isActive ? 600 : 400,
                      };
                    }}
                  >
                    <span>{t(page.title)}</span>
                  </NavLink>
                ))}

                <GradientDivider type="horizontal" />
                {isLoggedIn ? <ProfileMenu/> : null}
                {/* <HeaderIconButton onClick={() => { navigate('/login') }} desc="login">
                  <LoginIcon />
                </HeaderIconButton> */}
                <AccessibilityMenu />
                <LanguageSelector overrideBreakpoint />
              </Box>
            </Box>
          </SwipeableDrawer>
        </AppBar>
      </MuiThemeProvider>
    </header>
  );
};

export default PublicHeader;
